package com.eladfinish.bible.contest.ui.inputs

import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.text.selection.TextSelectionColors
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.TextFieldValue
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.colorAccent
import com.eladfinish.bible.contest.ui.layout.isShortWideWindow
import dev.icerock.moko.resources.compose.colorResource

@Composable
fun AppTextField(
    value: TextFieldValue,
    onValueChange: (TextFieldValue) -> Unit,
    label: String,
    keyboardType: KeyboardType,
    onImeAction: () -> Unit,
    enabled: Boolean = true,
    modifier: Modifier = Modifier,
    imeAction: ImeAction = ImeAction.Search,
    textStyle: TextStyle = MaterialTheme.typography.bodyLarge.copy(fontSize = 24.sp),
    singleLine: Boolean = true,
    errorMessage: String? = null,
    requestFocusOnStart: Boolean = true,
    focusRequester: FocusRequester = remember { FocusRequester() },
    leadingIcon: @Composable (() -> Unit)? = null,
    trailingIcon: @Composable (() -> Unit)? = null,
) {
    val accentColor = colorResource(SharedRes.colors.colorAccent)

    OutlinedTextField(
        modifier = modifier
            .fillMaxWidth()
            .padding(horizontal = 1.dp)
            .padding(top = 5.dp, bottom = 2.dp)
            .focusRequester(focusRequester),
        colors = OutlinedTextFieldDefaults.colors(
            focusedLabelColor = accentColor,
            cursorColor = accentColor,
            focusedBorderColor = accentColor,
            selectionColors = TextSelectionColors(
                handleColor = accentColor,
                backgroundColor = accentColor.copy(alpha = 0.4f),
            ),
        ),
        value = value,
        onValueChange = onValueChange,
        enabled = enabled,
        singleLine = singleLine,
        textStyle = textStyle,
        label = {
            Text(
                text = label,
            )
        },
        keyboardOptions = KeyboardOptions.Default.copy(
            imeAction = imeAction,
            keyboardType = keyboardType,
        ),
        keyboardActions = KeyboardActions(
            onSearch = {
                onImeAction()
            },
            onDone = {
                onImeAction()
            },
        ),
        leadingIcon = leadingIcon,
        trailingIcon = trailingIcon,
        isError = errorMessage != null,
        supportingText = {
            errorMessage?.let { message ->
                Text(
                    text = message,
                    color = MaterialTheme.colorScheme.error,
                )
            }
        },
    )

    // In a short window the keyboard would hide the screen before it was seen: it opens on a tap.
    // Saved: a rotation must not bring back a keyboard the user has closed, over their results.
    var didRequestFocusOnStart by rememberSaveable { mutableStateOf(false) }
    if (requestFocusOnStart && !didRequestFocusOnStart && !isShortWideWindow()) {
        LaunchedEffect(Unit) {
            didRequestFocusOnStart = true
            focusRequester.requestFocus()
        }
    }
}
